'use client';

import { Play } from 'lucide-react';

interface LiteYouTubeProps {
  id: string;
  title: string;
  /** When true the official player is mounted; until then only a thumbnail image loads. */
  playing: boolean;
  /** Start offset in seconds for the player (key moments). */
  start?: number;
  onPlay: () => void;
}

/**
 * YouTube facade: a thumbnail and a real <button> until the visitor clicks.
 * No YouTube script or iframe loads before that, so the embed costs one lazy
 * image on page load. The aspect-video box is fixed in both states, so swapping
 * in the iframe causes no layout shift. The player itself is YouTube's own
 * (nocookie) embed, untouched.
 */
export default function LiteYouTube({ id, title, playing, start = 0, onPlay }: LiteYouTubeProps) {
  if (playing) {
    const src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0${start > 0 ? `&start=${Math.floor(start)}` : ''}`;
    return (
      <div className="relative w-full aspect-video overflow-hidden rounded-xl bg-black">
        <iframe
          // Re-mount on a new start time so a key-moment click seeks reliably.
          key={`${id}-${start}`}
          src={src}
          title={title}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onPlay}
      aria-label={`Play video: ${title}`}
      className="group relative block w-full aspect-video overflow-hidden rounded-xl bg-gray-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#2cc3d1]"
    >
      <img
        src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
        alt=""
        loading="lazy"
        decoding="async"
        width={480}
        height={360}
        className="absolute inset-0 h-full w-full object-cover transition-opacity group-hover:opacity-90"
      />
      <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
        <span className="flex h-14 w-20 items-center justify-center rounded-2xl bg-red-600/90 shadow-lg transition-transform group-hover:scale-110 group-focus-visible:scale-110">
          <Play className="h-7 w-7 fill-white text-white" />
        </span>
      </span>
    </button>
  );
}
